import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Scale } from 'lucide-react';
import { Container, PageHeader, SplitView } from '@vnetwork/ui/layout';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  CardFooter,
  Skeleton,
} from '@vnetwork/ui/primitives';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import { Field } from '@vnetwork/ui/forms';
import {
  useTypedConfig,
  useCreateLoadBalancer,
  useQuotaValidator,
  parseFlavorName,
  type ConfigNetwork,
  type QuotaRequest,
} from '@/api/opennebula';
import { SectionCard, FlavorGrid, overQuotaMsg } from '../instances/instance-create/shared';
import { NetworkCard, isPublicNet } from '../instances/instance-create/StorageNetworkSection';
import { lbFlavors, LB_DEFAULT_FLAVOR } from './lb-flavors';

/**
 * Create a load balancer. Follows the Instance create layout: a `SplitView` of
 * stacked `SectionCard`s (identity → resource sizing → backend networks) beside
 * a sticky summary rail. The public frontend NIC is provisioned automatically,
 * so the network picker attaches private backend network(s) — one or many, the
 * same multi-NIC UX as instances. Payload: `{ instance: { name, flavor, networks } }`.
 */
export function LoadBalancerCreate() {
  const { serviceId } = useParams<{ serviceId: string }>();
  const navigate = useNavigate();
  const { show } = useToast();
  const base = `/services/compute/${serviceId}`;
  const { data: config, isLoading: cfgLoading } = useTypedConfig(serviceId, 'load-balancer');

  const flavors = useMemo(() => config?.flavors ?? [], [config]);
  const privateNetworks = useMemo(
    () => (config?.networks ?? []).filter((n) => !isPublicNet(n)),
    [config],
  );

  const ladder = useMemo(() => lbFlavors(flavors), [flavors]);

  const [name, setName] = useState('');
  const [flavor, setFlavor] = useState('');
  const [networks, setNetworks] = useState<ConfigNetwork[]>([]);
  const [showErrors, setShowErrors] = useState(false);

  // Seed from the config's initialize payload once it loads. The size defaults to
  // the smallest ladder tier (v2g-standard-2-4) rather than the backend's suggested
  // flavor, which may fall outside the LB ladder.
  useEffect(() => {
    if (!config) return;
    const init = config.initialize;
    setName((n) => n || init?.name || '');
    const tiers = lbFlavors(flavors);
    setFlavor((f) => f || tiers.find((x) => x.name === LB_DEFAULT_FLAVOR)?.name || tiers[0]?.name || '');
    setNetworks((prev) => (prev.length ? prev : privateNetworks.slice(0, 1)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);
  const create = useCreateLoadBalancer(serviceId!, {
    onSuccess: () => {
      show({ tone: 'success', title: 'Load balancer creating' });
      navigate(`${base}/load-balancers`);
    },
    onError: () => show({ tone: 'danger', title: 'Create failed' }),
  });

  const selectedFlavor = flavors.find((f) => f.name === flavor);
  const parsed = selectedFlavor ? parseFlavorName(selectedFlavor.name) : null;
  const flavorCores = selectedFlavor?.cores ?? parsed?.cores ?? 0;
  const flavorMemory = selectedFlavor?.memory ?? parsed?.memory ?? 0;

  const quota = useQuotaValidator(serviceId, { cores: flavorCores, memory: flavorMemory, volumes: 0, instances: 1 });
  const overQuota = !!selectedFlavor && quota.invalid;

  const issues = useMemo(() => {
    const out: string[] = [];
    if (!name.trim()) out.push('Give the load balancer a name.');
    if (!flavor) out.push('Choose a size.');
    if (!networks.length) out.push('Attach at least one private network.');
    return out;
  }, [name, flavor, networks]);

  const submit = () => {
    setShowErrors(true);
    if (issues.length || overQuota) return;
    create.mutate({ instance: { name: name.trim(), flavor, networks } });
  };

  if (cfgLoading) {
    return (
      <Container size="lg">
        <PageHeader
          icon={<Scale size={22} />}
          title="Create load balancer"
          description="Provision a managed HAProxy load balancer to distribute traffic across your instances."
        />
        <Skeleton style={{ height: 420 }} />
      </Container>
    );
  }

  return (
    <Container size="lg">
      <PageHeader
        icon={<Scale size={22} />}
        title="Create load balancer"
        description="Provision a managed HAProxy load balancer to distribute traffic across your instances."
      />

      <SplitView
        asideWidth="360px"
        aside={
          <LbSummaryRail
            name={name}
            flavor={flavor}
            cores={flavorCores}
            memory={flavorMemory}
            networks={networks}
            overQuota={overQuota}
            remaining={quota.remaining}
            issues={issues}
            showErrors={showErrors}
            pending={create.isPending}
            onCancel={() => navigate(`${base}/load-balancers`)}
            onSubmit={submit}
          />
        }
      >
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          <SectionCard
            title="Load balancer information"
            description="Give your load balancer an identifying name you will remember it by."
          >
            <Field label="Name" required>
              {(p) => (
                <Input
                  {...p}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="web-lb"
                  style={{ maxWidth: 420 }}
                  invalid={showErrors && !name.trim()}
                />
              )}
            </Field>
          </SectionCard>

          <SectionCard
            title="Resource Configuration"
            description="Pick a size — balanced standard tiers from 1 vCPU / 2 GB up to 32 vCPU / 64 GB."
          >
            <FlavorGrid flavors={ladder} value={flavor} onPick={setFlavor} emptyLabel="No sizes available." />
          </SectionCard>

          <NetworkCard
            serviceId={serviceId}
            all={privateNetworks}
            selected={networks}
            onChange={setNetworks}
            filter={(n) => !isPublicNet(n)}
            title="Backend networks"
            description="Attach the private network(s) where your backend instances live. A public frontend IP is assigned automatically."
            addPlaceholder="Add a private network…"
          />
        </div>
      </SplitView>
    </Container>
  );
}

// ---- Summary rail (mirrors the Instance create SummaryRail) ----------------

const groupLabel: React.CSSProperties = {
  fontSize: 'var(--vn-font-size-sm)',
  fontWeight: 700,
  color: 'var(--vn-fg-muted)',
  marginBottom: 'var(--vn-space-1)',
};
const valueText: React.CSSProperties = { fontSize: 'var(--vn-font-size-md)' };

function Group({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ padding: 'var(--vn-space-3) 0', borderBottom: '1px solid var(--vn-border-subtle)' }}>
      <div style={groupLabel}>{label}</div>
      {children}
    </div>
  );
}

function LbSummaryRail({
  name,
  flavor,
  cores,
  memory,
  networks,
  overQuota,
  remaining,
  issues,
  showErrors,
  pending,
  onCancel,
  onSubmit,
}: {
  name: string;
  flavor: string;
  cores: number;
  memory: number;
  networks: ConfigNetwork[];
  overQuota: boolean;
  remaining: QuotaRequest;
  issues: string[];
  showErrors: boolean;
  pending: boolean;
  onCancel: () => void;
  onSubmit: () => void;
}) {
  return (
    <Card style={{ position: 'sticky', top: 'var(--vn-space-4)' }}>
      <CardHeader>
        <CardTitle>Load balancer summary</CardTitle>
        <CardDescription>Review your configuration before provisioning.</CardDescription>
      </CardHeader>
      <CardBody>
        {overQuota && (
          <Alert tone="danger" title="Exceed Quota" style={{ marginBottom: 'var(--vn-space-3)' }}>
            Your configuration exceeds the current quota{overQuotaMsg(remaining) ? ` (needs ${overQuotaMsg(remaining)} more)` : ''}. Try a
            smaller size or contact support.
          </Alert>
        )}
        {showErrors && issues.length > 0 && (
          <Alert tone="danger" style={{ marginBottom: 'var(--vn-space-3)' }}>
            <ul style={{ margin: 0, paddingLeft: 16 }}>
              {issues.slice(0, 4).map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          </Alert>
        )}

        <Group label="Name">
          <div style={valueText}>{name || '—'}</div>
        </Group>
        <Group label="Size">
          <div style={valueText}>{flavor || '—'}</div>
          {flavor && (
            <>
              <div style={valueText}>{cores} vCPU</div>
              <div style={valueText}>{memory} GiB</div>
            </>
          )}
        </Group>
        <div style={{ paddingTop: 'var(--vn-space-3)' }}>
          <div style={groupLabel}>Backend networks ({networks.length})</div>
          {networks.length ? (
            networks.map((n) => (
              <div key={n.id} style={valueText}>
                {n.name}
                {n.prefix ? ` (${n.prefix})` : ''}
              </div>
            ))
          ) : (
            <div style={valueText}>—</div>
          )}
        </div>
      </CardBody>
      <CardFooter style={{ justifyContent: 'space-between' }}>
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="button" startIcon={<Scale size={15} />} onClick={onSubmit} disabled={pending || overQuota}>
          Create load balancer
        </Button>
      </CardFooter>
    </Card>
  );
}
